import { Tag } from "antd";
import { Progress } from "antd";
import styled from "styled-components";
import { GoldCoins } from "../../assets/svg/Svgs";
import { Button } from "../../components/common/Button";
import { Divider, RiskLevelTag } from "../../components/elements/Elements";
import { FundingCard } from "../../components/pages/loans/FundingCard";
import { API_LOAN_PRODUCTS } from "../../constants/apis";
import {
  FUND_LOAN,
  INVESTMENT_PLANS,
  SELECT,
  TEXT,
} from "../../constants/constants";
import { searchFilterField } from "../../constants/options";
import { PropOptions } from "../../utils/formUtils";
import {
  addSelectOption,
  addTagOption,
  currencyFormat,
  formatDate,
  getListRoute,
  getLoanGrade,
  stringify,
} from "../../utils/helpers";
import { openBottomSheet, openModalForm } from "../../utils/modalUtils";
import { primary10, PRIMARY_COLOR } from "../../constants/colors";
import { IsAdmin } from "../../utils/permissionUtils";

export const useInvestmentPlansList = () => {
  const filters = [
    searchFilterField,
    {
      field: "loan_duration",
      type: SELECT,
      options: ["1", "7", "14", "30", "60"].map((v) =>
        addSelectOption(v, `${v} days`)
      ),
    },
    {
      field: "min_amount",
      type: TEXT,
      props: PropOptions.number,
    },
    {
      field: "max_amount",
      type: TEXT,
      props: PropOptions.number,
    },
  ];
  return {
    listType: "grid",
    ListItem: InvestmentPlanItem,
    title: INVESTMENT_PLANS,
    listRoute: getListRoute(INVESTMENT_PLANS),
    api: API_LOAN_PRODUCTS,
    filters,
    subtitle: `Grow your wealth with ${stringify(INVESTMENT_PLANS)}`,
  };
};

const InvestFooter = ({ state }: { state?: any }) => {
  const { total_units, units_available, unit_price } = state || {};

  const progress = 100 - (units_available / total_units) * 100;
  const funded = unit_price * (total_units - units_available);
  const balance = unit_price * total_units - funded;

  return (
    <>
      <FundingCard progress={progress} balance={balance} funded={funded} />
      <Button
        onClick={() => {
          openModalForm({
            source: FUND_LOAN,
            state,
          });
        }}
        className="x100"
        disabled={units_available <= 0}
      >
        {units_available > 0 ? "Invest Now" : "Fully Funded"}
      </Button>
    </>
  );
};

// ─── Funding deadline helper ────────────────────────────────────
const DeadlineTag = ({ deadline }: { deadline: string }) => {
  if (!deadline) return null;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const daysLeft = Math.ceil(
    (deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysLeft < 0)
    return (
      <Tag color="red" className="font10" style={{ margin: 0 }}>
        Expired
      </Tag>
    );
  if (daysLeft <= 3)
    return (
      <Tag color="orange" className="font10" style={{ margin: 0 }}>
        {daysLeft}d left
      </Tag>
    );
  return (
    <Tag color="blue" className="font10" style={{ margin: 0 }}>
      {daysLeft}d left
    </Tag>
  );
};

export const InvestmentPlanItem = ({
  data,
  summary,
}: {
  data: any;
  summary?: boolean;
}) => {
  const {
    invest,
    purpose,
    net_return: netReturn,
    expected_payout,
    risk_level: riskLevel,
    duration_display,
    unit_price,
    backed_by,
    guaranteed_principal,
    total_units,
    units_available,
    payout_date,
    funding_deadline,
    is_fully_funded,
    loan_type,
  } = data || {};

  const { label, color } = getLoanGrade(riskLevel);

  const risk_level = <div style={{ color }}>{label}</div>;
  const net_return = (
    <Tag className="font11" style={{ margin: 0 }} color="green">
      {netReturn?.replace(".00", "")}
    </Tag>
  );

  // Funding progress
  const fundedUnits = (total_units || 0) - (units_available || 0);
  const fundingPercent =
    total_units > 0 ? Math.round((fundedUnits / total_units) * 100) : 0;

  const fields = {
    duration: `${duration_display} days`,
    payout: currencyFormat(expected_payout),
    risk_level,
  };

  const openDetails = () => {
    openBottomSheet({
      component: "details",
      title: "Investment Details",
      data: {
        purpose,
        type: loan_type === "device_finance" ? "Device Finance" : "Business Cash Loan",
        invest: currencyFormat(invest),
        unit_price: currencyFormat(unit_price),
        net_return,
        expected_payout: currencyFormat(expected_payout),
        risk_level: <RiskLevelTag value={riskLevel} />,
        duration: `${duration_display} days`,
        units_available,
        total_units,
        payout_date: formatDate(payout_date),
        funding_deadline: funding_deadline ? formatDate(funding_deadline) : "No deadline",
        guaranteed_principal,
        backed_by,
      },
      state: { ...data },
      footer: InvestFooter,
    });
  };

  return (
    <Wrapper
      className="flexColumn gap10 whiteBg p15 r10 capitalize"
      style={{
        width: summary ? 150 : "auto",
      }}
    >
      {/* TOP ROW */}
      <div className="flexSpaceCenter">
        <GoldCoins />
        <div className="flexNullCenter gap5">
          {net_return}
          {funding_deadline && <DeadlineTag deadline={funding_deadline} />}
        </div>
      </div>

      {/* HEADER */}
      <div>
        <div className="semiBold textDark">{currencyFormat(invest)}</div>
        <div className="font13 textDark ellipsis">{stringify(purpose)}</div>
      </div>

      {/* FUNDING PROGRESS — admin only */}
      {!summary && IsAdmin() && (
        <div>
          <div className="flexSpaceCenter mb3">
            <div className="font10 grey">
              {fundedUnits}/{total_units} units
            </div>
            <div className="font10 semiBold textDark">{fundingPercent}%</div>
          </div>
          <Progress
            percent={fundingPercent}
            size="small"
            showInfo={false}
            trailColor={primary10}
            strokeColor={
              is_fully_funded ? "#52c41a" : PRIMARY_COLOR
            }
          />
        </div>
      )}

      {/* MID */}
      <Divider />
      <div
        className="flexColumn gap5"
        style={{
          lineHeight: 1.2,
        }}
      >
        {Object.entries(fields).map(([key, value], i) => {
          return (
            <div key={i} className="flexSpaceCenter font11 grey gap10">
              <div>{stringify(key)}</div>
              <div className="textDark textEnd">{value}</div>
            </div>
          );
        })}
      </div>
      <Divider />

      {/* BOTTOM */}
      <Button
        className="font12 r20"
        onClick={openDetails}
        disabled={is_fully_funded}
      >
        {is_fully_funded ? "Fully Funded" : "Invest Now"}
      </Button>
    </Wrapper>
  );
};

const Wrapper = styled.div``;